import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertOctagon, AlertTriangle, CheckCircle2, Clock, Gauge, Plus } from 'lucide-react';
import { ServiceFormModal } from '../components/forms/ServiceFormModal';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { PageLoader } from '../components/ui/Spinner';
import { useMaintenanceOverview } from '../hooks/useApi';
import type { MaintenanceItem, MaintenanceRow, MaintenanceStatus } from '../types';
import { cn, formatDate, formatNumber } from '../utils/helpers';
import { t } from '../i18n';

// Meter: the fill carries severity; the track is a lighter step of the same hue. Always paired with icon + label.
const STATUS: Record<MaintenanceStatus, { label: string; fill: string; track: string; text: string; icon: typeof CheckCircle2 }> = {
  ok: { get label() { return t('V poriadku'); }, fill: '#0ca30c', track: 'rgb(12 163 12 / 0.15)', text: 'text-green-800', icon: CheckCircle2 },
  warning: { get label() { return t('Blíži sa'); }, fill: '#fab219', track: 'rgb(250 178 25 / 0.2)', text: 'text-amber-800', icon: Clock },
  due: { get label() { return t('Čoskoro'); }, fill: '#d03b3b', track: 'rgb(208 59 59 / 0.15)', text: 'text-red-700', icon: AlertTriangle },
  overdue: { get label() { return t('Po termíne'); }, fill: '#d03b3b', track: 'rgb(208 59 59 / 0.15)', text: 'text-red-700', icon: AlertOctagon },
};
const RANK: Record<MaintenanceStatus, number> = { overdue: 0, due: 1, warning: 2, ok: 3 };

function urgency(row: MaintenanceRow): number {
  const items = [row.service, row.inspection].filter(Boolean) as MaintenanceItem[];
  if (!items.length) return 99;
  return Math.min(...items.map((i) => RANK[i.status] * 10 - Math.min(i.progress ?? 0, 5)));
}

function remaining(item: MaintenanceItem): string {
  const parts: string[] = [];
  if (item.days_left !== null) parts.push(item.days_left < 0 ? t('{daysLeft} dní po termíne', { daysLeft: -item.days_left }) : item.days_left === 0 ? 'dnes' : t('o {daysLeft} dní', { daysLeft: item.days_left }));
  if (item.km_left !== null) parts.push(item.km_left < 0 ? t('prekročené o {kmLeft} km', { kmLeft: formatNumber(-item.km_left) }) : t('zostáva {kmLeft} km', { kmLeft: formatNumber(item.km_left) }));
  return parts.join(' · ');
}

function Meter({ title, item, carId, onAdd }: { title: string; item: MaintenanceItem | null; carId: number; onAdd: (carId: number) => void }) {
  if (!item) {
    return (
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{title}</p>
        <button type="button" onClick={() => onAdd(carId)} className="mt-1.5 text-sm text-secondary-400 hover:text-primary-700 hover:underline">
          {t('bez plánu – zadať servis')}
        </button>
      </div>
    );
  }
  const status = STATUS[item.status];
  const Icon = status.icon;
  // Without a computable interval the bar reflects the status alone
  const share = item.progress ?? (item.status === 'ok' ? 0.3 : item.status === 'warning' ? 0.8 : 1);
  const next = [item.next_date && formatDate(item.next_date), item.next_km !== null && `${formatNumber(item.next_km)} km`].filter(Boolean).join(' / ');
  return (
    <div className="min-w-0" title={t('{title}: {label}. Posledný {lastDate}, ďalší {next}. {remaining}', { title, label: status.label, lastDate: formatDate(item.last_date), next, item: remaining(item) })}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{title}</p>
        <p className={cn('flex items-center gap-1 text-xs font-medium', status.text)}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {status.label}
        </p>
      </div>
      <div
        className="mt-1.5 h-2.5 rounded-full"
        style={{ backgroundColor: status.track }}
        role="meter"
        aria-label={t('{title}: {label}', { title, label: status.label })}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(Math.min(share, 1) * 100)}
      >
        <div className="h-2.5 rounded-full" style={{ width: `${Math.max(Math.min(share, 1) * 100, 3)}%`, backgroundColor: status.fill }} />
      </div>
      <p className="mt-1 flex flex-wrap justify-between gap-x-3 text-xs text-secondary-600">
        <span>{remaining(item) || '–'}</span>
        <span className="text-secondary-500">{t('ďalší: {next}', { next: next || '–' })}</span>
      </p>
    </div>
  );
}

export function Maintenance() {
  const { data, isLoading } = useMaintenanceOverview();
  const [addingFor, setAddingFor] = useState<number | null>(null);
  if (isLoading) return <PageLoader />;

  const rows = [...(data ?? [])].sort((a, b) => urgency(a) - urgency(b) || a.car.license_plate.localeCompare(b.car.license_plate));
  const worst = (r: MaintenanceRow) => ([r.service, r.inspection].filter(Boolean) as MaintenanceItem[]).sort((a, b) => RANK[a.status] - RANK[b.status])[0]?.status;
  const counts = { overdue: 0, due: 0, warning: 0, ok: 0, none: 0 };
  for (const r of rows) counts[worst(r) ?? 'none'] += 1;

  return (
    <>
      <PageHeader
        title={t('Plán údržby')}
        subtitle={t('Ako ďaleko je každé vozidlo od servisu a od STK / EK – podľa dátumu alebo kilometrov, čo nastane skôr')}
        actions={
          <Button onClick={() => setAddingFor(0)}>
            <Plus className="h-4 w-4" />  {t('Zapísať servis')}
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        {(['overdue', 'due', 'warning', 'ok'] as MaintenanceStatus[]).map((s) => {
          const Icon = STATUS[s].icon;
          return (
            <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-secondary-200 bg-white px-3 py-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS[s].fill }} aria-hidden="true" />
              <Icon className={cn('h-3.5 w-3.5', STATUS[s].text)} aria-hidden="true" />
              {STATUS[s].label} <strong className="tabular-nums">{counts[s]}</strong>
            </span>
          );
        })}
        <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary-200 bg-white px-3 py-1.5 text-secondary-600">
          {t('Bez plánu')} <strong className="tabular-nums">{counts.none}</strong>
        </span>
      </div>

      {!rows.length ? (
        <Card>
          <EmptyState icon={<Gauge className="h-10 w-10" />} title={t('Žiadne vozidlá v prevádzke')} />
        </Card>
      ) : (
        <Card padding="none">
          <ul className="divide-y divide-secondary-100">
            {rows.map((row) => (
              <li key={row.car.id} className="grid grid-cols-1 gap-4 px-5 py-4 md:grid-cols-[12rem_1fr_1fr] md:gap-8">
                <div className="min-w-0">
                  <Link to={`/cars/${row.car.id}?tab=services`} className="font-mono font-semibold text-primary-700 hover:underline">
                    {row.car.license_plate}
                  </Link>
                  <p className="truncate text-sm text-secondary-500">
                    {row.car.brand} {row.car.model}
                  </p>
                  <p className="text-xs text-secondary-500">{formatNumber(row.car.current_km)} km</p>
                </div>
                <Meter title={t('Servis')} item={row.service} carId={row.car.id} onAdd={setAddingFor} />
                <Meter title={t('STK / EK')} item={row.inspection} carId={row.car.id} onAdd={setAddingFor} />
              </li>
            ))}
          </ul>
          <p className="border-t border-secondary-100 px-5 py-2 text-xs text-secondary-500">
            
            {t('Pás ukazuje, koľko z intervalu od posledného servisu už uplynulo. Červená = do 30 dní alebo 1 500 km, príp. po termíne; oranžová = viac ako 75 % intervalu. Plán sa berie z posledného servisného záznamu s vyplneným „ďalší servis“ (STK / EK z typu „Kontrola / STK / EK“).')}
          </p>
        </Card>
      )}
      <ServiceFormModal isOpen={addingFor !== null} carId={addingFor || undefined} onClose={() => setAddingFor(null)} />
    </>
  );
}
